// Agent Setup training module for RESIDENCE | eXp Realty.
// Content comes from Kelly's Wix course. Each step: title, body paragraphs,
// optional links and an optional video placeholder.
// RISE-only steps (Solo Agent, Personalized Brand) are intentionally left out here.

export const agentSetupModule = {
  id: 'agentSetup',
  title: 'Agent Setup',
  intro:
    'Welcome to the Agent Setup module. Work through each step in order. Your progress is saved to your account.',
  sections: [
    {
      id: 'welcome',
      title: 'Welcome to RESIDENCE | eXp Realty',
      steps: [
        {
          id: 'about-training-site',
          title: 'About this Training Site',
          videoPlaceholder: 'Welcome video (about 16 min): Agent Setup overview',
          body: [
            'This training site was created by Kelly Olin and is independent from eXp Realty.',
            'Log in with the user ID you created for this site. Your account is your first.last name with the password you chose. Save that password somewhere safe.',
          ],
        },
      ],
    },
    {
      id: 'business-setup',
      title: 'Business Setup',
      steps: [
        {
          id: 'welcome-to-exp',
          title: 'Welcome to eXp!',
          body: [
            'These trainings will guide you step by step through everything you need to set up as a new agent at eXp Realty.',
            'Once your application is submitted, click Next Step below to get started.',
          ],
        },
        {
          id: 'exp-okta',
          title: 'eXp Okta (Your Back Office)',
          body: [
            'Once you get the "Welcome to eXp" email, you can log in to your back office at exprealty.okta.com.',
            'Your login is your first name, then a dot, then your last name, @exprealty.com. For example: kelly.olin@exprealty.com.',
          ],
          links: [{ label: 'exprealty.okta.com', url: 'https://exprealty.okta.com/' }],
        },
        {
          id: 'boldtrail',
          title: 'Request BoldTrail (your website and database)',
          body: [
            'BoldTrail is the software we use to manage our websites, track client information, and send email and text campaigns. This is called a CRM, or Customer Relationship Management tool.',
            'Request access RIGHT AWAY. It can take 7 to 10 business days after you request it.',
            'Step 1: Go to exptoolkit.com/crmofchoice, log in if prompted, select BOLD TRAIL, select SOLO AGENT, and opt in. You will get an email once your account is created. Training on setting up BoldTrail comes in a later course.',
            'Step 2 (put a reminder on your calendar to come back in 2 to 3 days): Connect your account to our team so you get our premium team theme and team templates. Use the team request form and enter Kelly Olin and Kelly\'s eXp email, then RESIDENCE as the team. The bottom part of the form is your information.',
            'Note: If you are outside the SWFL MLS, do NOT join the local RESIDENCE | eXp Realty team in BoldTrail. You can still be part of our team in general, but your website should show your local MLS results.',
          ],
          links: [
            { label: 'Exptoolkit CRM request', url: 'https://exptoolkit.com/crmofchoice' },
            {
              label: 'Team request form (RESIDENCE in BoldTrail)',
              url: 'https://exprealty.formstack.com/forms/kvcore_request_to_add_agent_account_to_kvcore_team',
            },
          ],
        },
        {
          id: 'boldtrail-api-token',
          title: 'BoldTrail and Resi: the team connection',
          body: [
            'You do not need to make or paste a BoldTrail token. Your team leader connects BoldTrail to Resi for the whole team.',
            'Your new BoldTrail leads come into Resi automatically. There is nothing for you to set up.',
            'Never share a BoldTrail token in a chat, text, or email, even if someone asks.',
          ],
          links: [{ label: 'BoldTrail login', url: 'https://boldtrail.exprealty.com/' }],
        },
        {
          id: 'local-realtor-board',
          title: 'Your Local Realtor Board',
          body: [
            'Applying for your local board of Realtors is mandatory. In Southwest Florida, register with NABOR, since our office and team are registered with that board.',
            'On your application, enter: Agency/Office: Exp Realty, LLC. Address: 12251 Itec Park Dr Ste. 107, Fort Myers, FL 33913. Office Code (if asked): NEXPLUX.',
            'If you are not located in Florida, use your state\'s joining guide to find your Realtor board.',
            'NABOR is how you get access to the MLS to list and view homes, schedule showings through ShowingTime, and get your Supra eKey for lock boxes.',
            'There will be a mandatory MLS course, Ethics Training, and Orientation. Get those classes on your calendar as soon as your membership is set up to stay in good standing.',
          ],
        },
        {
          id: 'email',
          title: 'Email',
          body: [
            'eXp assigns you an official eXp email address, but there is no eXp email system we use. Instead, you use your Gmail account.',
            'Incoming mail sent to your eXp address is automatically forwarded to the Gmail account on file. You do not need to set anything up for this.',
            'We highly recommend setting up a separate Gmail account for your real estate business. Create it at gmail.com, then log in to eXp Enterprise and update your profile with the new email address.',
            'To send outgoing emails from your Gmail account as your eXp address, follow the eXp support tutorial linked below.',
          ],
          links: [
            { label: 'Watch: How to Create a Gmail Account', url: 'https://youtu.be/1aUTqoiECJE' },
            { label: 'gmail.com', url: 'https://gmail.com' },
            {
              label: 'Send from Gmail as your eXp address (eXp support)',
              url: 'https://support.expcloud.com/portal/en/kb/articles/configure-gmail-to-send-as-your-exp-email',
            },
          ],
        },
        {
          id: 'autopay',
          title: 'Setup AutoPay to Save Time',
          body: [
            'Within a few days of activation, you will get an email about paying your $149 invoice. Click PAY NOW to enter your payment information.',
            'After your payment is confirmed, you will be prompted to create a password and set up an account. Do this.',
            'Once you are logged in, click SETUP AUTOPAY in the upper right. This will save you a lot of time. Enter your payment information again, accept the terms, and you are set. You will not have to worry about another eXp invoice.',
          ],
        },
        {
          id: 'exp-world',
          title: 'eXp World',
          body: [
            'eXp World is our virtual reality office. It is a website you can access from any device. Move through rooms across a virtual campus to join meetings and trainings, or speak directly with the support brokers who can help.',
            'To access eXp World, go to exp.world/welcome and follow the prompts to create your avatar.',
            'Most agents use it to speak with a broker during business hours. After hours or for non-emergencies, you can email brokers at fl.broker@exprealty.net.',
            'To speak with a broker during business hours, go to the GO TO section, select US BROKER ROOMS, then select FLORIDA.\nMake sure your microphone is on, because someone will greet you.\nUse the up arrow to move your avatar toward the desk.\nThey will likely ask you to take a ticket and fill out a form with your question.\nTell them you are brand new and it is your first time there.\nThey will walk you through the process.',
          ],
          links: [{ label: 'exp.world/welcome', url: 'https://exp.world/welcome' }],
        },
        {
          id: 'team-calendar',
          title: 'Our Team Calendar',
          body: [
            'Subscribe to our RESIDENCE Team Google Calendar for our live and Zoom group trainings and events.',
            'You will find all of our team calendars under the Account Profile tab on this site, in Team Calendars. To start, subscribe to the main one.',
            'Click the ADD TO GOOGLE CALENDAR link to subscribe.',
          ],
        },
        {
          id: 'youtube-channel',
          title: 'Our Team YouTube Channel',
          body: [
            'We have a YouTube channel for our team with several unlisted (private) playlists of content just for team members.',
            'RISE Express Playbacks: playbacks from past RISE Zoom events. Sensitive meetings are removed before posting, so these load directly to this training site under the right topic.',
            'FastCap Playbacks. Video Editing "CapCut" Tutorials. YouTube Building Tutorials.',
          ],
          links: [
            { label: 'Team meeting playbacks', url: 'https://www.youtube.com/playlist?list=PLBLnnOxF_VxtR3IcaXRA9vRe31oLn2Imd' },
          ],
        },
        {
          id: 'residence-office',
          title: 'RESIDENCE | eXp Realty Office',
          body: [
            'Kelly Olin and Matt Bresee have invested in a beautiful office space you can use 24/7.',
            'The office address is 12251 Itec Park Drive Suite 107, Fort Myers, FL 33913. You may use this address on any of your marketing or professional profiles in place of your home address.',
            'The office is off Alico Road, across from the back resident entrance of Wild Blue.',
            'To access the office, there is a Supra lock box on the door. Once you have Supra access from your board, you can open the office anytime with this lock box. Enter your personal code you set up with Supra, then push the bottom of the lock box up to activate it.',
          ],
        },
        {
          id: 'showing-time-keys',
          title: 'Showing Time & E Keys',
          body: [
            'ShowingTime is the mandatory tool you use to schedule showings of homes. Download the ShowingTime app on your phone from the iTunes or Android app store. Once you are set up with your local board, log in with your credentials.',
            'To access electronic lock boxes, you will use Supra. Supra needs to be activated through your local Realtor board. Once you are set up with your board, look for an email or contact them directly to set up your Supra account. There is an activation and/or monthly fee, and it is worth it.',
            'You will likely need to go into NABOR (or your local board) for one of their mandatory classes soon. While you are there, stop in the shop and ask them to help you get set up with ShowingTime and Supra.',
          ],
        },
        {
          id: 'professional-profiles',
          title: 'Professional Profiles',
          body: [
            'Having a strong online presence on all of the real estate websites is essential. When someone googles for a Realtor or asks AI to recommend someone, your profiles will show up in those results and give you instant credibility.',
            'Go to the following sites and create your agent profiles: Zillow, Trulia, Realtor.com, and Homes.com.',
            'Google Business is another profile. It is more technical to set up, so it has its own section in this training, right after Business Setup.',
          ],
        },
        {
          id: 'voicemail',
          title: 'Voicemail',
          body: [
            'When clients or even friends call you and get your voicemail, this is the perfect opportunity to let them know you are a professional. Change your voicemail to something like this:',
            '"Hi! This is [Your Name] with RESIDENCE | eXp Realty. Thank you for calling. Your call is important to me! Please leave me a message and I will return your call as soon as possible."',
          ],
        },
        {
          id: 'hub-intro',
          title: 'What Is the eXp Hub?',
          body: [
            "The eXp Hub is eXp's online home for agents. Use it to find agents and their profiles, search posts and groups, read your conversations and notifications, and search the eXp Knowledge base for company policy, programs, commissions, and tools.",
          ],
          links: [],
        },
        {
          id: 'resi-home-screen',
          title: 'Put Resi on Your Phone',
          body: [
            "Add Resi to your phone's home screen so it opens with one tap, just like an app. It is not an app from the App Store: it is a shortcut to the Resi website, so there is nothing to download or update.",
            "iPhone: Open Safari (it must be Safari) and go to resisystem.com. Sign in. Tap the Share button (the square with the arrow pointing up), scroll down and tap Add to Home Screen, then tap Add.",
            "Android: Open Chrome and go to resisystem.com. Sign in. Tap the menu (the three dots in the top corner), tap Add to Home screen (it may say Install app), then tap Add.",
            "Look for the gold R icon named Resi on your home screen. Tap it any time to open Resi.",
          ],
        },
        {
          id: 'verify-sending-email',
          title: 'Verify Your Email for Sending',
          body: [
            'Resi can send your client emails (deal updates and email blasts) so they come from your own email address. Before it can, your address has to be verified once.',
            "Step 1: Let Kelly know which email address you want clients to see your emails come from, and we'll set it up.",
            "Step 2: You'll get a one-time confirmation email at that address. Open it and click the link. That is all you need to do.",
            "Step 3: Kelly then ticks 'Verified sender' next to your name in Resi. From then on, your client emails come from your own address.",
            "Until then, your client emails show as 'Your Name via RESIDENCE' and come from a team address. Replies still come straight to you, so you never miss a client reply.",
          ],
        },
        {
          id: 'niche-news',
          title: 'Niche News: Your Daily Local Headlines',
          body: [
            "Niche News is your own little news feed inside Resi. You pick the areas, communities, and topics you care about, and Resi rounds up fresh articles about them for you every day. It's an easy way to stay in the know so you always have something timely to talk about with your clients and sphere.",
            "Step 1: Open your Profile and scroll to Niche news interests.",
            "Step 2: Type an interest and tap Add. Add as many as you like! Think towns (Estero, Kingston), neighborhoods or communities you farm, and topics like new construction or mortgage rates. Tap the little x on any interest to remove it.",
            "Step 3: That's it! Every morning Resi checks the news for each of your interests and saves the most recent articles (about 10 per interest).",
            "Where to find them: On your Dashboard, look for News for you. You'll see each article's headline, where it's from, and the date. Tap an interest at the top to see just that topic, and tap a headline to open the full article in a new tab.",
            "Tip: Just added a new interest? Give it until the next morning for articles to show up. These articles are for you and the team only. When you share content with clients, keep linking to our own website.",
          ],
        },
        {
          id: 'connect-your-ai',
          title: 'Connect Your AI',
          body: [
            "Connect Resi directly to the AI app you use (Claude, ChatGPT, Grok, Grok Bot, Gemini, Perplexity, or another MCP client) so you can ask in plain language, like \"What is my commission so far this year?\" or \"Add Rick Winkler as a buyer, condo, 2 bed, 2 bath, $200k to $300k.\"",
            "Your AI signs in as you and follows your role. Agents can read and change only their own deals, pipeline, leads, tasks and account. Your AI should confirm with you before it adds or changes anything.",
            "Before you start: know your Resi email and password (the ones you use on resisystem.com). Most AI apps connect by asking you to sign in to Resi. No code to copy.",
            "Step 1: In Resi, open your Profile. Scroll down to Connect Your AI.",
            "Step 2: Tap the \"Your AI app\" box. Pick your app: Grok Bot, Grok, Claude, ChatGPT, Gemini, Perplexity, or Other.",
            "Step 3: Read the \"Before you start\" note. It tells you if your plan can connect, and if you must use a computer.",
            "Step 4: Follow the numbered steps for your app, one at a time. The Resi address is always https://resisystem.com/mcp",
            "Step 5: When a Resi sign-in page opens, type your Resi email and password, tap Sign in, then tap Approve.",
            "Step 6: Test it. Ask your AI: \"Using Resi, what are my tasks today?\" You should see your own tasks.",
            "Grok Bot in short: in a Grok Bot chat, type \"Add a custom MCP server named Resi at https://resisystem.com/mcp\". Tap Approve, tap Authorize on the card, sign in to Resi, and tap Approve.",
            "If it doesn't work: (1) Use your Resi email and password, not your Google or AI app password. (2) Forgot your Resi password? Use Forgot password on resisystem.com first. (3) The address must be exactly https://resisystem.com/mcp. (4) Says Unauthorized? Remove Resi from the AI app and add it again. (5) Can't find the Add button? Your plan may not allow it; try Claude or Grok Bot. (6) Still stuck? Send Kelly a screenshot.",
            "Tip: If you lose your phone or stop using an app, tap Revoke in Connect Your AI. Access stops right away.",
          ],
        },
      ],
    },
    {
      id: 'google-business',
      title: 'Google Business Profile',
      steps: [
        {
          id: 'gbp-before-you-start',
          title: 'Before You Start',
          body: [
            "A Google Business Profile is the listing that shows up when someone searches your name on Google Search or Google Maps. Google is strict about these listings, and most suspensions come from a few avoidable mistakes, so read this section before you create anything.",
            "Create only one profile for yourself. Before you start, search your name with real estate on Google Maps. If a listing already exists for you, claim or edit that one instead of creating a second. Duplicate profiles are one of the most common reasons listings get suspended.",
            "Use a dedicated business Gmail account for your profile, and keep using that same account for future edits and verification.",
            "Common reasons for suspension: duplicate listings, a home address shown publicly, extra keywords added to the business name, and information that does not match your real-world business. The steps below are built to avoid each of these.",
          ],
        },
        {
          id: 'gbp-create-profile',
          title: 'Create Your Profile',
          body: [
            "Sign in to your Google account and go to google.com/business. Click Manage now.",
            "Enter your business name exactly as you are known professionally, such as your full name. Do not add keywords like Top Agent or Coach to the name, because that is a common trigger for suspension. Check Google's current guidelines before you add REALTOR® or your brokerage name.",
            "Choose Service area business as your business type, not a business with a street address, and choose Real Estate Agent as your category. Google does not have a REALTOR® category.",
            "Add every area you serve, such as Estero, Bonita Springs, Fort Myers, and Naples.",
            "Add the phone number and website where clients should reach you.",
          ],
        },
        {
          id: 'gbp-address-privacy',
          title: 'Protect Your Address',
          body: [
            "Do not publish your home address. If you work from home, keep your home address off the public profile. Google asks whether you serve customers at your address. If you do not, choose the option that hides your address and shows only your service areas.",
            "Only list a business address if clients can visit you there. Google still uses the address you enter for verification, so make it accurate, and ask Google to keep it off the public listing.",
            "If your profile already shows a home address you cannot edit, do not create a new profile to fix it. Contact Google Business Profile support from the account that owns the existing listing.",
          ],
        },
        {
          id: 'gbp-verify',
          title: 'Verify Your Profile',
          body: [
            "Choose a verification method. Google will send a code by postcard, phone, or another option it offers you. Postcards take several days, and phone or text codes are usually faster.",
            "Enter the code in your Google Business Profile. You can keep editing your profile while you wait for verification.",
            "Before you verify, make sure your name, address settings, and service areas are final. Changing them after verification can trigger a review.",
          ],
        },
        {
          id: 'gbp-customize',
          title: 'Customize Your Profile',
          body: [
            "Add the services you offer, such as buyer representation, seller representation, relocation, and new construction.",
            "Add your business hours for when you are available, and turn on messaging so people who find you on Google can reach you.",
            "Add a business description. Use your real estate bio, written in plain language without keyword stuffing.",
            "Add your logo and a professional headshot. Optional: add photos of homes you have sold, or of your office if clients visit it. Every photo should be real, so do not use stock images or photos of homes you did not represent.",
          ],
          links: [
            { label: 'Google Business Profile checklist (Drive)', url: 'https://drive.google.com/file/d/1-NSP0lDacyfMldzmERuTxF-NxjATB7d3/view?usp=sharing' },
          ],
        },
        {
          id: 'gbp-reviews',
          title: 'Reviews and Verified Agent',
          body: [
            "Ask past clients to leave a Google review. Use the Online Review Templates from the team's member training to send a text or email with your review link.",
            "Never write reviews yourself, never create fake accounts to leave reviews, and never offer gifts or money in exchange for reviews. Any of these can get your profile suspended.",
            "Once your profile is verified and you have at least 5 reviews, you can apply to become a Verified Agent and run Local Service Ads.",
          ],
          links: [
            { label: 'Local Service Ads', url: 'https://ads.google.com/local-services-ads' },
          ],
        },
        {
          id: 'gbp-keep-it-clean',
          title: 'Keep It Clean',
          body: [
            "Keep one profile. If you think you have a duplicate, do not create a replacement. Report the duplicate to Google and let the original stay live.",
            "Keep your name, address, and phone number the same across your website, Realtor board, and other profiles.",
            "If Google suspends your profile, do not create a new one. Read the notice, fix the issue it names, and appeal from the existing account. Ask the team for help before you make changes.",
          ],
        },
      ],
    },
    {
      id: 'boldtrail-kvcore',
      title: 'BoldTrail',
      steps: [
        {
          id: 'kvcore-intro',
          title: 'Introduction to BoldTrail',
          body: [
            "BoldTrail is the platform we use to manage our website and our customer database. It is the number one tool you will use to grow your business.",
            "It is critical that you become very familiar with BoldTrail, its functionality, and how you work in it every day. This guide walks you through how to get trained on BoldTrail. In the next modules, you will learn how to apply it to generate leads for your business.",
          ],
        },
        {
          id: 'kvcore-checklist',
          title: 'After This Course: Your Checklist',
          body: [
            "When you finish this course, make sure you have completed the following.",
            "Log in to BoldTrail at www.boldtrail.com.",
            "Edit your profile. In the BoldTrail dashboard, click your name in the upper right corner, select My Profile, then Edit Profile. Update your photo, contact information, and social accounts, saving as you go. Then scroll down to the About Me and Signature boxes and add your text to your account.",
            "Start uploading your contacts to your Databank. You can add contacts one by one under the Contacts tab on the left, or do a bulk import by selecting Lead Engine, then Bulk Import.",
          ],
          links: [
            { label: 'BoldTrail (www.boldtrail.com)', url: 'https://www.boldtrail.com' },
          ],
        },
      ],
    },
    {
      id: 'branding',
      title: 'Branding & Marketing Materials',
      steps: [
        {
          id: 'branding-intro',
          title: 'Getting Started',
          body: [
            'You are welcome to begin this module, but it will be hard to start designing materials until you are completely set up as an agent with eXp Realty and have:',
            '1. Access to your eXp Backoffice "Okta" account.',
            '2. Your eXp email.',
            '3. BoldTrail site access. You can set up BoldTrail later; you just need login access.',
            'In the meantime, be a sponge and check out what is in store!',
          ],
        },
        {
          id: 'brand-standards',
          title: 'Brand Standards',
          body: [
            'A brand helps clients recognize us as an authority and leader in local real estate. We all benefit from a well-recognized brand that is seen as ethical, proactive, professional, customer-service minded, innovative, and collaborative.',
            'Logos: Use the RESIDENCE logos on all of your marketing materials, digital and print. This matters for the brand and for compliance with eXp and NAR.',
            'Colors: Black, white, and gold (C8A50D). Use these in your marketing for a cohesive look.',
            'Font: Roboto. Use it for headers or accents to match the logo font.',
            'Title: When introducing yourself to agents or clients, on the phone, in person, or online, say "[Your Name], REALTOR with RESIDENCE | eXp Realty." Note the lowercase e and uppercase X in eXp, capitalize every letter in RESIDENCE, and use the | symbol between RESIDENCE and eXp.',
            'Quick tip: Create a shortcut on your iPhone. Go to Settings, General, Keyboard, Text Replacement, then +. Phrase: RESIDENCE | eXp Realty. Shortcut: Res. Now typing "Res" brings up the full phrase.',
            'Brand templates (email signature, Facebook header, signage, and more) are in the Marketing Center on our team website. You are also free to design your own marketing as long as it follows these guidelines.',
            'Questions? Reach out to Kelly Olin any time.',
          ],
          images: [
            { src: '/resi-logo-black.png', alt: 'RESIDENCE logo, black', label: 'RESIDENCE logo (black)' },
            { src: '/resi-logo-white.png', alt: 'RESIDENCE logo, white', label: 'RESIDENCE logo (white)' },
            { src: '/residence-exp-logo.png', alt: 'RESIDENCE | eXp Realty logo', label: 'RESIDENCE | eXp Realty logo' },
          ],
          links: [
            { label: 'RESIDENCE Marketing Center', url: 'https://homebuyersguide.my.canva.site/residence' },
            {
              label: 'RESIDENCE | eXp Realty Brand Standards (Google Doc)',
              url: 'https://docs.google.com/document/d/1-9zteradK-6ROGeellQR5O7ayQ_ck2tEiAs_qxMkCjs/edit',
            },
          ],
        },
        {
          id: 'headshots',
          title: 'Headshots',
          body: [
            'As a Realtor, your face is your brand, regardless of your brokerage or team. A professional photo you use across all of your marketing is essential, and it is the first step before you create any marketing materials.',
            'You do not need a photo shoot. Upload one of your favorite photos of yourself to ChatGPT and use the prompt below to create several versions with different outfits, poses, and backgrounds. The result must still look like you.',
          ],
          prompt:
            'Using this photo of me, create a set of professional real estate headshots.\nKeep my face, hair, skin tone and features exactly as they are; do not change my identity or age.\nCreate 6 versions: (1) head-and-shoulders headshot, (2) half-body portrait, (3) full-body portrait, each in a solid black suit or dress (or a black blazer and blouse), (4) the same headshot in a solid white or cream top, (5) a relaxed, friendly smile in a light tan or gold-toned outfit, and (6) a confident, approachable portrait in front of a clean, blurred, neutral background with soft natural light.\nKeep the lighting soft and even, the background clean and uncluttered, and the style polished and professional, suitable for real estate marketing.',
          links: [{ label: 'ChatGPT', url: 'https://chatgpt.com' }],
        },
        {
          id: 'vanity-domains',
          title: 'Vanity Domains',
          body: [
            'As an eXp agent, you will automatically be assigned a domain, which is your website address: firstnamelastname.exprealty.com.',
            'If you would like your own customized domain, such as sellingswflroida.com or SWFLHomeswithJen.com, purchase the domain from GoDaddy and then link it to your BoldTrail website.',
            'Please note: You are responsible for paying GoDaddy for your domain purchase and any renewals. You will also need to buy the BoldTrail Custom Domain add-on. The cost is $11 per month with a 3-month minimum, and it can take a couple of weeks to sync. In the meantime, you can personalize your BoldTrail website until your domain is live.',
            'Make a decision now about whether you want to invest in a custom domain, so you know how to design your marketing materials.',
            'Instructions for connecting your vanity domain to BoldTrail are in the Lead Generation section of the Knowledge Center.',
          ],
          links: [{ label: 'GoDaddy', url: 'https://www.godaddy.com' }],
        },
        {
          id: 'canva-marketing',
          title: 'How to Use Canva for Marketing Designs',
          body: [
            'Canva is a critical tool to create beautiful marketing pieces for your business. Throughout this training you will find links to Canva templates that are already designed. You just need to personalize them with your own photo and contact information. However, you can use Canva to create your own designs as well, following our brand guidelines.',
            'We recommend this playlist of Canva tutorials, which covers everything you need to know about using the tool.',
            'I have several Canva templates for frequently used documents already created. They are loaded to the Marketing section of this site. However, eXp also offers all agents a Canva Premium account. Log in with your eXp Realty email, and there are HUNDREDS of eXp-created templates waiting for you!',
          ],
          links: [
            { label: 'Canva tutorial playlist', url: 'https://www.youtube.com/watch?v=V9LtRF6EbyY&list=PLATYfhN6gQz8GiTG_nUxVar8ycrt9hJxL' },
          ],
        },
        {
          id: 'social-media-setup',
          title: 'Social Media Setup',
          body: [
            'You may or may not already be active on social media, but as a business owner it is essential that you have a presence. Agents who have been around for years and have a large network of past clients may get referrals without a social media presence. However, new agents and top agents all have a strong social media presence.',
            'There are many social media platforms, such as Facebook, Instagram, YouTube, X (formerly Twitter), TikTok, and new ones that pop up all the time. For this training, we suggest you start with Facebook and optionally add Instagram.',
            'Facebook has the largest and broadest audience. Instagram is more discoverable for new clients to find you.',
            'Watch the tutorials below to learn more about each platform and how to set up your accounts.',
            'If you are branding as RESIDENCE | eXp Realty, you will find a RESIDENCE | eXp Realty Facebook header template below.',
          ],
          links: [
            { label: 'RESIDENCE Marketing Center', url: 'https://homebuyersguide.my.canva.site/residence' },
            { label: 'Setting up your Instagram Account (video)', url: 'https://drive.google.com/file/d/1KPkcC7xa7TuVmtVXlztIcQpIU5E402Q7/view?usp=sharing' },
          ],
        },
        {
          id: 'business-cards',
          title: 'Business Cards & Folders',
          body: [
            'As a new agent with eXp Realty, you will need business cards. We also recommend getting at least one package of eXp folders for marketing materials for your future clients.',
            'For the RESIDENCE | eXp Realty brand, we have already designed business cards in Canva. Log in, change the information and photo to yours, and save the file.',
            'As a new agent, eXp offers business card printing and possibly other marketing materials as part of the New Agent Starter Kit. The new agent package changes periodically, so check what is currently offered.',
            'Find the pre-designed RESIDENCE | eXp Realty business card templates in the Marketing Center. Use the Marketing Templates link below to access them.',
            'Review the eXp business card guidelines before you edit your design.',
            'Once you have edited the file to your liking, click SHARE in the upper right, then Download. Select the file type and page, and the file will download to your computer.',
            'When you are ready to place your order, go to the new agent starter kit page. Click Redeem Start Kit promotion, then scroll down. Choose the Business Card option to upload your own design or use one of their pre-designed templates.',
            'You will get 1,000 STANDARD business cards and a pack of eXp folders for $52, with shipping included. Add them to your cart, complete your order with your contact information, and you are all set!',
          ],
          links: [
            { label: 'RESIDENCE Marketing Center', url: 'https://homebuyersguide.my.canva.site/residence' },
            { label: 'eXp Business Card Guidelines', url: 'https://docs.google.com/document/d/1bo94MSNws3UexFJMmVD6vMWA4N-PKEVHxwRFPkpPglY/edit?usp=sharing' },
            { label: 'New Agent Starter Kit (order business cards and folders)', url: 'https://exp.buildasignenterprise.com/new-agent-starter-kit' },
          ],
        },
        {
          id: 'signs',
          title: 'Open House & Directional Signs',
          body: [
            'Most gated communities in Southwest Florida either do not allow signs or have very specific sign requirements. For this reason, we do not recommend buying nice For Sale signs until you have a listing and know that community\'s requirements.',
            'We DO recommend buying OPEN HOUSE signs right away. Open houses are one of the first activities for new agents to practice interacting with clients and generating leads.',
            'If you are branding under Team RESIDENCE | eXp, use the Canva template below to create your sign design.',
            'Once you have your sign design, we use Thin Line Graphics locally to print our signs. Email them to place your order.',
            'Note for directionals: Make sure you send them a file with a directional arrow pointing both ways, so they print back to back correctly.',
          ],
          links: [
            { label: 'RESIDENCE Marketing Center', url: 'https://homebuyersguide.my.canva.site/residence' },
            { label: 'Email Thin Line Graphics: sales@thinlinegraphics.com', url: 'mailto:sales@thinlinegraphics.com' },
          ],
        },
        {
          id: 'announce-warm-network',
          title: 'Announce Your Business to Your Warm Network',
          body: [
            "Announcing your business to the people you already know is one of the most important things you will do. Over the long term, most of your transactions will likely come from these people and the people they refer to you.",
            "Post the announcement on your social media profiles, such as Facebook, Instagram, and LinkedIn, and in any community groups you belong to. Then send personal messages to these people by text, DM, or email. Personalize each message. Ask people to reply with their email or phone number so you can add them to your database for upcoming market news.",
            "You can use AI tools such as ChatGPT to create an image for your announcement post. Use your real photo and your real name, and make sure any image you post accurately represents you and your business.",
            "Here is an example announcement you can personalize. Kelly texted and DMed her contacts with a message like this one.",
            "Challenge: create a list of 100 local people you can announce your business to this week. Pull up your recent phone log, old holiday card lists, neighborhood directory, and similar sources, and build a spreadsheet of 100 people you can personally text, email, or DM.",
          ],
          links: [
            { label: 'RESIDENCE Marketing Center', url: 'https://homebuyersguide.my.canva.site/residence' },
          ],
        },
        {
          id: 'chatgpt-images',
          title: 'Using ChatGPT to Create Images',
          body: [
            "ChatGPT can create marketing images for your posts, announcements, and headshot variations. This step shows you how to get good results and what to watch for.",
            "How to start: open ChatGPT, upload a clear, well-lit photo of yourself, or describe the image you want if you are creating a graphic without a photo. Then type a prompt that says what the image is for, the style you want, and what must stay the same. The headshot prompt in the previous step is a good model to copy.",
            "Tip: be specific. Name the setting, the outfit, the lighting, and the background. Say what the image is for, such as an Instagram post or a Facebook announcement, so the size and layout fit.",
            "Tip: ask for one change at a time. If the result is close, ask for a single adjustment, such as a different background or a warmer tone, instead of starting over.",
            "Tip: keep your identity accurate. Use a real photo of yourself, and keep your face, hair, and features exactly as they are. Do not post an image that makes you look like someone else or a different age.",
            "Tip: check every result before you post. Look closely at hands, teeth, jewelry, and any text in the image, because AI often misspells words or distorts details. Fix anything that looks wrong or ask for a new version.",
            "Tip: do not use AI images to represent a property. Never post a generated image as if it were a real home, a listing, or a sold property. Use real photos of properties you represent.",
            "Tip: keep your branding consistent. Use the RESIDENCE colors and logo in your prompts, and place your logo and contact information on the final image in Canva, not in the AI output.",
          ],
        },
        {
          id: 'share-templates',
          title: 'Share Your Templates with the Team',
          body: [
            "Click the link below to view templates shared by RESIDENCE team members, and share your favorites by filling out a row yourself.",
            "To add your own template: upload your image to Google and paste a shareable link for a preview. Then add it to Canva and apply Magic Layers. Grab the LIVE TEMPLATE link and add it to the sheet so your fellow team members can use the template too.",
            "Don't worry, they won't be able to edit your original template.",
          ],
          links: [
            { label: 'Team template sheet (Google Sheets)', url: 'https://docs.google.com/spreadsheets/d/1-Tc7sWrCruokNTPxUOtOAIE8aMcoOaOV2s3XxGoEMho/edit?usp=sharing' },
          ],
        },
      ],
    },
    {
      id: 'next-steps',
      title: 'Next Steps',
      steps: [
        {
          id: 'next-steps-new-realtors',
          title: 'Next Steps for New Realtors',
          body: [
            'If you are new to real estate, your next training module should be New Agents. Open the Training section and select New Agents to continue.',
            'Your path through training looks like this:',
            '1. Agent Setup',
            '2. New Agent Training',
            '3. Then choose one of two paths: Lead Gen & Management, or FastCap (self-paced)',
            '4. Working with Buyers',
            '5. Then choose one of two paths: Sellers & Listings, or Transaction Management',
          ],
        },
      ],
    },
  ],
};

export const allSteps = agentSetupModule.sections.flatMap((section) =>
  section.steps.map((step) => ({ ...step, sectionId: section.id, sectionTitle: section.title }))
);
